/**
 * DeliveryDetailView.js - UPGRADED
 * Modern Driver App UI with POD (Proof of Delivery) support
 * Style: Lieferando/Uber/Volt inspired
 */

import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Phone,
  Navigation,
  User,
  Package,
  Loader2,
  AlertCircle,
  ExternalLink,
  StickyNote,
  Copy,
  CheckCircle,
  Edit,
  X,
  RotateCcw,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import cashxDeliveryService from '@/services/cashxDeliveryService';

const DeliveryDetailView = ({ open, onClose, deliveryId, orderInfo }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [proofStatus, setProofStatus] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [skipNote, setSkipNote] = useState('');
  const [savingProof, setSavingProof] = useState(false);
  
  const signatureRef = useRef(null);

  useEffect(() => {
    if (open && deliveryId) {
      loadDetails();
      loadProofStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deliveryId]);

  const loadDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await cashxDeliveryService.getDriverViewDetails(deliveryId);

      if (result.success === false) {
        setError(result.error);
        return;
      }

      // Handle DSGVO redaction
      if (result.dsgvo_redacted || result.status === 'delivered') {
        setDetails({
          ...result,
          dsgvo_redacted: true
        });
      } else {
        setDetails(result);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadProofStatus = async () => {
    try {
      const status = await cashxDeliveryService.getProofStatus(deliveryId);
      setProofStatus(status);
    } catch (err) {
      console.error('Proof status error:', err);
      // Not critical, continue without POD
    }
  };

  const handleError = (err) => {
    if (err.response) {
      const status = err.response.status;
      if (status === 401) {
        setError('Security Check fehlgeschlagen. Bitte Support kontaktieren.');
      } else if (status === 403) {
        setError('Zugriff verweigert. Diese Lieferung ist einem anderen Fahrer zugewiesen.');
      } else if (status === 404) {
        setError('Lieferung wurde in CashX nicht gefunden.');
      } else if (status === 429) {
        setError('Zu viele Anfragen. Bitte 60 Sekunden warten und erneut versuchen.');
      } else if (status === 503 || status === 504) {
        setError('CashX ist momentan nicht erreichbar. Bitte Support kontaktieren oder später versuchen.');
      } else {
        setError('Fehler beim Laden der Details');
      }
    } else {
      setError('Verbindungsfehler. Bitte Internetverbindung prüfen.');
    }
    console.error('Load details error:', err);
  };

  const openGoogleMaps = () => {
    if (!details) return;

    const mapsUrl = details.maps_url || (() => {
      const { lat, lng, address } = details;
      
      if (lat && lng) {
        return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      } else if (address) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      }
      return null;
    })();

    if (mapsUrl) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile && details.lat && details.lng) {
        const geoUrl = `geo:${details.lat},${details.lng}?q=${details.lat},${details.lng}`;
        const openAttempt = window.open(geoUrl, '_blank');
        
        if (!openAttempt) {
          window.open(mapsUrl, '_blank');
        }
      } else {
        window.open(mapsUrl, '_blank');
      }
      
      toast.success('Navigation wird geöffnet...');
    } else {
      toast.error('Keine Adresse verfügbar');
    }
  };

  const callCustomer = () => {
    if (!details) return;

    const callUrl = details.call_url || (details.phone ? `tel:${details.phone.replace(/\s|-/g, '')}` : null);

    if (callUrl) {
      window.location.href = callUrl;
      toast.success('Anruf wird gestartet...');
    } else {
      toast.error('Keine Telefonnummer verfügbar');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} kopiert`);
    }).catch(() => {
      toast.error('Kopieren fehlgeschlagen');
    });
  };

  const handleSaveSignature = async () => {
    if (signatureRef.current.isEmpty()) {
      toast.error('Bitte Unterschrift hinzufügen');
      return;
    }

    setSavingProof(true);
    try {
      const signatureSVG = signatureRef.current.toDataURL('image/svg+xml');
      const base64 = signatureSVG.split(',')[1];

      await cashxDeliveryService.saveProof(deliveryId, {
        type: 'signature',
        signature_svg_base64: base64
      });

      toast.success('Liefernachweis gespeichert', {
        icon: '✅'
      });
      setShowProofModal(false);
      loadProofStatus(); // Reload status
    } catch (err) {
      toast.error('Fehler beim Speichern', {
        description: err.message || 'Bitte erneut versuchen'
      });
    } finally {
      setSavingProof(false);
    }
  };

  const handleSkipSignature = async () => {
    if (!skipReason) {
      toast.error('Bitte Grund auswählen');
      return;
    }

    setSavingProof(true);
    try {
      await cashxDeliveryService.saveProof(deliveryId, {
        type: 'skipped',
        reason: skipReason,
        note: skipNote
      });

      toast.success('Grund gespeichert');
      setShowSkipModal(false);
      setShowProofModal(false);
      loadProofStatus();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingProof(false);
    }
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  // Check states
  const isActive = details?.status === 'claimed' || details?.status === 'in_delivery';
  const isCompleted = details?.status === 'delivered';
  const showProofButton = isActive && proofStatus?.enabled && !proofStatus?.exists;

  // Status badge config
  const getStatusConfig = () => {
    if (!details) return null;
    
    const configs = {
      claimed: { label: '🚗 Übernommen', color: 'border-blue-500 text-blue-400 bg-blue-500/10' },
      in_delivery: { label: '📦 Unterwegs', color: 'border-green-500 text-green-400 bg-green-500/10' },
      delivered: { label: '✅ Ausgeliefert', color: 'border-emerald-500 text-emerald-400 bg-emerald-500/10' }
    };
    
    return configs[details.status] || configs.claimed;
  };

  const statusConfig = getStatusConfig();

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md max-h-[90vh] overflow-y-auto p-0">
          {/* Modern Header */}
          <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 z-10">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package className="h-6 w-6 text-blue-500" />
                Lieferdetails
              </h2>
              {orderInfo && (
                <span className="text-xs text-zinc-500">
                  #{orderInfo.order_number || orderInfo.order_id}
                </span>
              )}
            </div>
            
            {/* Status Badge + Mini Stepper */}
            {statusConfig && (
              <div className="flex items-center gap-2">
                <Badge className={`${statusConfig.color} px-3 py-1 font-medium`}>
                  {statusConfig.label}
                </Badge>
                
                {/* Mini Stepper */}
                <div className="flex items-center gap-1 text-xs text-zinc-600">
                  <div className={`w-2 h-2 rounded-full ${details?.status === 'claimed' || details?.status === 'in_delivery' || details?.status === 'delivered' ? 'bg-blue-500' : 'bg-zinc-700'}`} />
                  <ChevronRight className="h-3 w-3" />
                  <div className={`w-2 h-2 rounded-full ${details?.status === 'in_delivery' || details?.status === 'delivered' ? 'bg-green-500' : 'bg-zinc-700'}`} />
                  <ChevronRight className="h-3 w-3" />
                  <div className={`w-2 h-2 rounded-full ${details?.status === 'delivered' ? 'bg-emerald-500' : 'bg-zinc-700'}`} />
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4">
            {/* Loading State */}
            {loading && (
              <div className="py-12 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
                <p className="text-zinc-400">Lade Lieferdetails...</p>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="py-8 text-center space-y-4">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <div>
                  <p className="text-red-400 mb-2 font-medium">{error}</p>
                  {error.includes('Support') && (
                    <p className="text-xs text-zinc-500 mt-2">
                      Fehlercode: {error.includes('401') ? 'AUTH_FAILED' : 'SERVICE_ERROR'}
                    </p>
                  )}
                </div>
                <Button
                  onClick={loadDetails}
                  variant="outline"
                  className="border-zinc-700"
                >
                  Erneut versuchen
                </Button>
              </div>
            )}

            {/* Completed Delivery - Modern DSGVO Screen */}
            {!loading && !error && isCompleted && (
              <div className="py-8 space-y-6">
                <div className="text-center space-y-4">
                  <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="h-10 w-10 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-emerald-400 mb-2">
                      Lieferung abgeschlossen
                    </h3>
                    <p className="text-zinc-400 mb-4">
                      Kundendaten sind aus Datenschutzgründen nicht mehr verfügbar.
                    </p>
                  </div>
                </div>

                {/* Order Summary (without PII) */}
                {orderInfo && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-3">
                    <h4 className="font-semibold text-sm text-zinc-400 mb-2">Bestellübersicht</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">Bestellung</span>
                      <span className="text-white">#{orderInfo.order_number || orderInfo.order_id}</span>
                    </div>
                    {orderInfo.amount && (
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Betrag</span>
                        <span className="text-white">{orderInfo.amount}€</span>
                      </div>
                    )}
                    {orderInfo.tip && (
                      <div className="flex justify-between text-sm">
                        <span className="text-zinc-500">Trinkgeld</span>
                        <span className="text-green-400">{orderInfo.tip}€</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={onClose}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Zurück zur Liste
                  </Button>
                  <Button
                    onClick={() => {/* Navigate to scanner */}}
                    variant="outline"
                    className="w-full border-zinc-700"
                  >
                    Neue Lieferung scannen
                  </Button>
                </div>
              </div>
            )}

            {/* Active Delivery - Premium Contact Card + CTAs */}
            {!loading && !error && isActive && details && (
              <div className="space-y-4">
                {/* Delivery Note - PROMINENT */}
                {details.note && (
                  <div className="flex items-start gap-3 p-4 bg-amber-500/20 rounded-xl border-2 border-amber-500/50 shadow-lg">
                    <StickyNote className="h-6 w-6 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-amber-300 mb-1">⚠️ Wichtiger Hinweis</p>
                      <p className="text-amber-100 font-medium text-sm leading-relaxed">
                        {details.note}
                      </p>
                    </div>
                  </div>
                )}

                {/* Premium Contact Card */}
                <div className="bg-gradient-to-br from-zinc-950 to-zinc-900 border border-zinc-800 rounded-2xl p-5 shadow-xl space-y-4">
                  {/* Customer Name with Avatar */}
                  {details.customer_name && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                          <User className="h-6 w-6 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-0.5">Kunde</p>
                          <p className="text-white font-semibold text-lg">{details.customer_name}</p>
                        </div>
                      </div>
                      {/* Phone CTA */}
                      {details.phone && (
                        <Button
                          onClick={callCustomer}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 h-10 w-10 p-0 rounded-full"
                        >
                          <Phone className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Address */}
                  {details.address && details.address !== '***' && (
                    <div className="flex items-start gap-3 pt-3 border-t border-zinc-800">
                      <MapPin className="h-5 w-5 text-zinc-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-zinc-500 mb-1">Lieferadresse</p>
                        <p className="text-white leading-relaxed">{details.address}</p>
                      </div>
                      <Button
                        onClick={() => copyToClipboard(details.address, 'Adresse')}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Primary CTAs - Modern Design */}
                <div className="space-y-3">
                  {/* Navigation Button */}
                  {((details.lat && details.lng) || details.address || details.maps_url) && (
                    <Button
                      onClick={openGoogleMaps}
                      disabled={details.dsgvo_redacted}
                      className={`w-full h-16 text-white rounded-2xl shadow-lg transition-all ${
                        details.dsgvo_redacted 
                          ? 'bg-zinc-700 cursor-not-allowed opacity-50' 
                          : 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl hover:scale-[1.02]'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-3 w-full">
                        <Navigation className="h-6 w-6" />
                        <div className="text-left flex-1">
                          <div className="font-bold">Navigation starten</div>
                          <div className="text-xs opacity-80">Google Maps / Apple Maps</div>
                        </div>
                        <ExternalLink className="h-5 w-5" />
                      </div>
                    </Button>
                  )}

                  {/* Call Button */}
                  {(details.phone || details.call_url) && (
                    <Button
                      onClick={callCustomer}
                      disabled={details.dsgvo_redacted}
                      className={`w-full h-16 text-white rounded-2xl shadow-lg transition-all ${
                        details.dsgvo_redacted 
                          ? 'bg-zinc-700 cursor-not-allowed opacity-50' 
                          : 'bg-green-600 hover:bg-green-700 hover:shadow-xl hover:scale-[1.02]'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-3 w-full">
                        <Phone className="h-6 w-6" />
                        <div className="text-left flex-1">
                          <div className="font-bold">Kunde anrufen</div>
                          {details.phone && !details.dsgvo_redacted && (
                            <div className="text-xs opacity-80">{details.phone}</div>
                          )}
                        </div>
                      </div>
                    </Button>
                  )}

                  {/* Proof of Delivery Button */}
                  {showProofButton && (
                    <Button
                      onClick={() => setShowProofModal(true)}
                      variant="outline"
                      className="w-full h-14 border-2 border-emerald-600 text-emerald-400 hover:bg-emerald-600/10 rounded-2xl"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" />
                      ✅ Lieferung bestätigen
                    </Button>
                  )}
                </div>

                {/* Quick Actions Row */}
                <div className="flex gap-2 pt-2">
                  {details.address && (
                    <Button
                      onClick={() => copyToClipboard(details.address, 'Adresse')}
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-zinc-400 hover:text-white"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Adresse
                    </Button>
                  )}
                  {details.phone && (
                    <Button
                      onClick={() => copyToClipboard(details.phone, 'Nummer')}
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-zinc-400 hover:text-white"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Nummer
                    </Button>
                  )}
                </div>

                {/* DSGVO Info */}
                {details.dsgvo_redacted && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-200 text-center">
                      ℹ️ Kundendaten nicht mehr verfügbar (DSGVO)
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Proof of Delivery Modal */}
      {showProofModal && (
        <Dialog open={showProofModal} onOpenChange={setShowProofModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md h-[80vh] flex flex-col p-0">
            <DialogHeader className="px-6 py-4 border-b border-zinc-800">
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-500" />
                Liefernachweis
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 flex flex-col p-6 overflow-hidden">
              <p className="text-sm text-zinc-400 mb-4">
                Bitte Unterschrift des Empfängers hier eintragen:
              </p>

              {/* Signature Canvas */}
              <div className="flex-1 bg-white rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center relative overflow-hidden">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-full',
                    style: { touchAction: 'none' }
                  }}
                  backgroundColor="white"
                />
                <div className="absolute top-2 right-2">
                  <Button
                    onClick={clearSignature}
                    size="sm"
                    variant="ghost"
                    className="bg-zinc-900/80 text-white hover:bg-zinc-900"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <p className="text-xs text-zinc-500 text-center mt-2">
                Bitte hier unterschreiben
              </p>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="border-t border-zinc-800 p-4 space-y-2 bg-zinc-900">
              <Button
                onClick={handleSaveSignature}
                disabled={savingProof}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
              >
                {savingProof ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Speichere...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Speichern
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowSkipModal(true)}
                variant="ghost"
                className="w-full text-zinc-400"
              >
                Unterschrift nicht möglich
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Skip Reason Modal */}
      {showSkipModal && (
        <Dialog open={showSkipModal} onOpenChange={setShowSkipModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Grund angeben</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Warum keine Unterschrift?</label>
                <select
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-white"
                >
                  <option value="">Bitte auswählen...</option>
                  <option value="customer_not_present">Kunde nicht anwesend</option>
                  <option value="contactless_delivery">Kontaktlose Lieferung</option>
                  <option value="customer_refused">Kunde lehnt ab</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Optionale Notiz</label>
                <textarea
                  value={skipNote}
                  onChange={(e) => setSkipNote(e.target.value)}
                  placeholder="Z.B. 'An Tür abgestellt'"
                  rows={3}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-3 text-white placeholder-zinc-600"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleSkipSignature}
                  disabled={!skipReason || savingProof}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {savingProof ? 'Speichere...' : 'Speichern'}
                </Button>
                <Button
                  onClick={() => {
                    setShowSkipModal(false);
                    setSkipReason('');
                    setSkipNote('');
                  }}
                  variant="outline"
                  className="flex-1 border-zinc-700"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default DeliveryDetailView;
